import { useState } from 'react';
import {
  useNavigate,
  useParams,
  useRouter,
  useSearch,
} from '@tanstack/react-router';
import { Button } from 'src/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { T } from 'gt-react';
import useAuth from 'src/api/auth/useAuth';
import { useGetAgentRunTimeline } from 'src/api/rpc/hooks/agent-definitions/useAgentDefinitions';
import {
  acceptedTimelineRun,
  resolveLinkedRun,
  runIdToLoad,
  timelineFetchSettled,
  type LinkedRunView,
} from '../agentPageRuns';
import { searchForRun } from '../agentRunHistoryFilter';
import type { AgentPageRuns } from '../useAgentPageRuns';
import type { AgentRunSummary } from '@erp/rpc';
import { AgentRunDetailCard } from './AgentRunDetailCard';
import { AgentRunHistory } from './AgentRunHistory';

/** The run history beside the selected run's details. */
export function AgentRunsSection({
  history,
  agentDefinitionId,
  agentName,
}: {
  history: AgentPageRuns['history'];
  agentDefinitionId: string;
  agentName: string;
}) {
  const { runs } = history;
  const { currentUser } = useAuth();
  const { agentPageId } = useParams({
    from: '/authenticated/agent-pages/$agentPageId',
  });
  const search = useSearch({ from: '/authenticated/agent-pages/$agentPageId' });
  const navigate = useNavigate({ from: '/agent-pages/$agentPageId' });
  const router = useRouter();
  // A `?run=` link names the run. Until one is picked, follow the live/newest
  // run so a freshly launched run opens itself.
  const linkedRunId = search.run;
  const linkedQuery = useGetAgentRunTimeline(runIdToLoad(linkedRunId, runs));
  const isPlaceholder = linkedQuery.isPlaceholderData === true;
  const view = resolveLinkedRun({
    runs,
    historyReady: !history.isPending,
    linkedRunId,
    fetchedRun: acceptedTimelineRun(
      linkedRunId,
      linkedQuery.data?.run,
      isPlaceholder,
    ),
    fetchSettled: timelineFetchSettled(
      linkedQuery.isFetched === true,
      isPlaceholder,
    ),
    agentDefinitionId,
  });
  const [inspecting, setInspecting] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const selectedRun = selectedRunOf(view);
  const shareUrl = selectedRun
    ? new URL(
        router.buildLocation({
          to: '/agent-pages/$agentPageId',
          params: { agentPageId },
          search: searchForRun(search, selectedRun.id),
        }).href,
        window.location.origin,
      ).toString()
    : null;

  return (
    <div
      className={
        inspecting
          ? 'grid min-w-0 gap-3'
          : 'grid items-start gap-4 lg:grid-cols-[minmax(260px,1fr)_minmax(0,2fr)]'
      }
    >
      {inspecting && (
        <Button
          variant="outline"
          size="sm"
          className="w-fit"
          onClick={() => setHistoryOpen(!historyOpen)}
          aria-expanded={historyOpen}
        >
          <T context="agent run inspector history toggle">History</T>
        </Button>
      )}
      <div hidden={inspecting && !historyOpen}>
        <AgentRunHistory
          runs={runs}
          total={history.total}
          hiddenCount={history.hiddenCount}
          categoryCounts={history.categoryCounts}
          filter={history.filter}
          onFilterChange={history.onFilterChange}
          selectedRunId={selectedRun?.id ?? null}
          onSelectRun={(id) => {
            setInspecting(false);
            setHistoryOpen(false);
            void navigate({
              search: (previous) => searchForRun(previous, id),
            });
          }}
          onLoadMore={history.onLoadMore}
          isLoadingMore={history.isFetching}
        />
      </div>
      <SelectedRunPane
        view={view}
        shareUrl={shareUrl}
        agentDefinitionId={agentDefinitionId}
        agentName={agentName}
        organizationId={currentUser?.organizationId}
        onInspectionChange={setInspecting}
      />
    </div>
  );
}

function SelectedRunPane({
  view,
  shareUrl,
  agentDefinitionId,
  agentName,
  organizationId,
  onInspectionChange,
}: {
  view: LinkedRunView;
  shareUrl: string | null;
  agentDefinitionId: string;
  agentName: string;
  organizationId: string | undefined;
  onInspectionChange: (open: boolean) => void;
}) {
  if (view.kind === 'selected' && shareUrl) {
    return (
      <AgentRunDetailCard
        key={view.run.id}
        run={view.run}
        agentDefinitionId={agentDefinitionId}
        agentName={agentName}
        shareUrl={shareUrl}
        onInspectionChange={onInspectionChange}
        organizationId={organizationId}
      />
    );
  }
  if (view.kind === 'loading') {
    return (
      <section className="flex items-center justify-center rounded-lg border border-border p-6">
        <Spinner />
      </section>
    );
  }
  if (view.kind === 'missing') {
    return (
      <section className="flex items-center justify-center rounded-lg border border-dashed border-border p-6 text-sm text-muted-foreground">
        <T context="agent page when a linked run id cannot be opened on this agent">
          This run could not be loaded. It may belong to a different agent, or
          it no longer exists.
        </T>
      </section>
    );
  }
  return (
    <section className="flex items-center justify-center rounded-lg border border-dashed border-border p-6 text-sm text-muted-foreground">
      <T context="agent page placeholder beside an empty run history">
        Run details appear here once the agent has run.
      </T>
    </section>
  );
}

function selectedRunOf(view: LinkedRunView): AgentRunSummary | null {
  return view.kind === 'selected' ? view.run : null;
}
